import { useCallback } from "react";
import { apiNiches } from "@/common/services";
import { showNotification } from "@/common/utils/showNotification";
import { useUserStore } from "@/common/store/userStore";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { metricsBus, NicheDiscoverySource, NicheSubscriptionSource } from "@/common/buses";
import { useQuery } from "@/common/hooks/useQuery";

type TUseFollowNicheQueries = {
  nicheId: string;
  following: boolean;
  origin: NicheSubscriptionSource;
  sendFollowState: (id: string, status: boolean) => void;
};

const isNicheDiscoverySource = (source: string | null): source is NicheDiscoverySource => {
  return source === "all_niches";
};

export const useFollowNicheQueries = ({
  nicheId,
  following,
  origin,
  sendFollowState
}: TUseFollowNicheQueries) => {
  const username = useUserStore((state) => state.currentUser?.username);
  const { navigateSignIn } = useAuthNavigate();
  const source = useQuery().get("source");

  const onUnFollow = useCallback(async () => {
    try {
      await apiNiches.unfollow(nicheId);
      showNotification({
        title: "Not following this niche anymore.",
        variant: "info",
      });
      sendFollowState(nicheId, !following);
      metricsBus.emit("leaveNicheClicked", { nicheId });
      metricsBus.emit("nicheUnsubscribed", { nicheId, origin });
    } catch (e) {
      console.error("Could not change niche status.");
    }
  }, [nicheId, following, origin, sendFollowState]);

  const onFollow = useCallback(async () => {
    try {
      await apiNiches.follow(nicheId);
      showNotification({
        title: "Joined!",
        description: "You can view all your joined niches in your profile",
        variant: "success",
      });
      sendFollowState(nicheId, !following);
      metricsBus.emit("joinNicheClicked", { nicheId });
      metricsBus.emit("nicheSubscribed", {
        nicheId,
        origin,
        source: isNicheDiscoverySource(source) ? source : null,
      });
    } catch (e) {
      console.error("Could not change niche status.");
    }
  }, [nicheId, following, origin, sendFollowState, source]);

  const onFollowAction = useCallback(() => {
    if (!username) {
      metricsBus.emit("joinNicheClickedAnonymously", { nicheId });
      navigateSignIn();
      return;
    }

    following ? onUnFollow() : onFollow();
  }, [username, following, onUnFollow, onFollow]);

  return { onFollowAction };
};
